import fetch from "cross-fetch";
import { refreshAccessToken } from "./iam/operations";
import { getAccessToken } from "./iam/selectors";

const SUCCESS_STATUSES = [200, 201, 204];

// One refresh at a time. A page usually asks several endpoints at once, and when the access token
// has run out they all come back 401 together; without this they would each start their own
// exchange for a new token.
let runningRefresh = null;

const refreshAccessTokenOnce = (dispatch) => {
    if (!runningRefresh) {
        const forget = () => {
            runningRefresh = null;
        };

        runningRefresh = Promise.resolve(dispatch(refreshAccessToken())).then(
            (accessToken) => {
                forget();
                return accessToken;
            },
            (error) => {
                forget();
                throw error;
            }
        );
    }

    return runningRefresh;
};

const mergeAuthRequestOptions = (options, getState) => {
    return {
        ...options,
        headers: {
            ...options.headers,
            Authorization: `Bearer ${getAccessToken(getState())}`,
            "Content-Type": "application/json"
        }
    };
};

const sessionHasEnded = () => ({
    message: "ERROR: Your session has ended. Please log in again.",
    status: 401
});

const settle = (response) => {
    if (response.status === 409) {
        return Promise.reject(response);
    }

    if (!SUCCESS_STATUSES.includes(response.status)) {
        return Promise.reject({
            message: "ERROR: Cannot perform this request!",
            status: response.status
        });
    }

    if (response.status === 204) {
        return Promise.resolve(undefined);
    }

    return response.json();
};

export const authRequest = (url, dispatch, getState, options = {}) => {
    const send = () =>
        fetch(url, mergeAuthRequestOptions(options, getState)).catch((error) => {
            return Promise.reject({ message: `Network error: ${error.toString()}` });
        });

    return send().then((response) => {
        if (response.status !== 401) {
            return settle(response);
        }

        return refreshAccessTokenOnce(dispatch).then((accessToken) => {
            if (!accessToken) {
                return Promise.reject(sessionHasEnded());
            }

            return send().then((retried) => {
                // The freshly issued token was refused as well. Asking for another one would
                // repeat this exchange for as long as the page stays open, so the call ends here.
                if (retried.status === 401) {
                    return Promise.reject(sessionHasEnded());
                }

                return settle(retried);
            });
        });
    });
};
